import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Trash2 } from 'lucide-react';

/**
 * Admin confirmation dialog (light theme, matches admin panel).
 * Props: open, title, message, confirmLabel, cancelLabel, variant ('danger'|'warning'),
 *        onConfirm, onCancel, loading
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'warning',
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const isDanger = variant === 'danger';
  const Icon = isDanger ? Trash2 : AlertTriangle;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="confirm-dialog"
          onClick={onCancel}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${isDanger ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[#1F2933] mb-1.5">{title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{message}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-[#64748B] hover:bg-[#F1F5F9] transition-colors"
                data-testid="confirm-cancel-btn"
              >
                {cancelLabel}
              </button>
              <button
                onClick={onConfirm}
                disabled={loading}
                className={`px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60 ${isDanger ? 'bg-red-600 hover:bg-red-700' : 'bg-[#1E88E5] hover:bg-[#1976D2]'}`}
                data-testid="confirm-ok-btn"
              >
                {loading ? 'Please wait…' : confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmDialog;
